<template>
  <div class="movement-detail-page">
    <!-- Barra superior con acciones y estado -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles del Movimiento</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-chip v-if="!loading" small :color="getStatusColor(movimiento.status)">
          <v-icon left x-small>{{ getStatusIcon(movimiento.status) }}</v-icon>
          {{ translateStatus(movimiento.status) }}
        </v-chip>

        <v-btn v-if="!loading && canChangeStatus() && HAS_PERMISSION('aceptar_movimiento')" small outlined color="white" class="ml-2" @click="acceptMovimiento">
          <v-icon left small>mdi-arrow-up-bold</v-icon>
          Avanzar estado
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Contenido principal -->
    <v-container>
      <!-- Estado de carga -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
        <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
          Cargando detalles del movimiento...
        </div>
      </div>

      <template v-else>
        <!-- Información del movimiento -->
        <v-card class="mb-4 elevation-1">
          <v-card-title class="d-flex align-center pb-1">
            <v-avatar color="primary lighten-4" size="42" class="mr-3">
              <v-icon color="primary">mdi-car</v-icon>
            </v-avatar>
            <div>              <div class="text-h5 font-weight-medium">
                {{ (movimiento.vehicle.brand || 'Sin marca') }} {{ (movimiento.vehicle.model || 'Sin modelo') }}
              </div>
              <div class="caption grey--text d-flex align-center">
                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                {{ movimiento.delivery_date ? formatDateHours(movimiento.delivery_date) : formatDateHours(movimiento.datetime) }}
                <v-icon x-small class="mx-1">mdi-circle-small</v-icon>
                {{ movimiento.type === 'entry' ? 'Entrada' : movimiento.type === 'move' ? 'Movimiento' : movimiento.type === 'exit' ? 'Salida' : 'No especificado' }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <div>
              <v-btn v-if="canEditMovimiento" icon small color="primary" @click="showMovimientoForm(movimiento)" class="mr-1"
                :disabled="movimiento.status === 'completed'">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn v-if="canDeleteMovimiento" icon small color="error" @click="confirmDeleteMovimiento"
                :disabled="movimiento.status === 'completed'">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Trayecto (origen-destino) -->
          <v-card-text class="py-2">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-map-marker-path</v-icon>              <div class="text-body-1">
                <span class="font-weight-medium grey--text text--darken-1">{{ movimiento.type === 'entry' ? 'Sin ubicación' : movimiento.sourceLocation ? movimiento.sourceLocation.name : 'No especificado' }}</span>
                <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                <span class="font-weight-medium">{{ movimiento.destinationLocation ? movimiento.destinationLocation.name : 'No especificado' }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Pestañas para organizar la información -->
        <v-card class="elevation-1">
          <v-tabs v-model="activeTab" background-color="transparent" grow>
            <v-tab>
              <v-icon left>mdi-information-outline</v-icon>
              Detalles
            </v-tab>
            <v-tab>
              <v-icon left>mdi-car-info</v-icon>
              Vehículo
            </v-tab>
            <v-tab>
              <v-icon left>mdi-image-multiple</v-icon>
              Imágenes
              <v-badge v-if="imageFiles && imageFiles.length" color="primary"
                :content="imageFiles.length" offset-x="10" offset-y="-4" small></v-badge>
            </v-tab>
            <v-tab>
              <v-icon left>mdi-file-document-multiple</v-icon>
              Documentos
              <v-badge v-if="documentFiles && documentFiles.length" color="primary"
                :content="documentFiles.length" offset-x="10" offset-y="-4" small></v-badge>
            </v-tab>
          </v-tabs>

          <v-divider></v-divider>

          <!-- Contenido de pestañas -->
          <v-tabs-items v-model="activeTab">
            <!-- Pestaña de detalles del movimiento -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <!-- Columna izquierda -->
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="40%">
                              <v-icon small color="primary" class="mr-1">mdi-swap-horizontal-bold</v-icon>
                              Tipo
                            </td>                            <td>
                              {{ movimiento.type === 'entry' ? 'Entrada' :
                                movimiento.type === 'move' ? 'Movimiento' : 
                                movimiento.type === 'exit' ? 'Salida' : 'No especificado' }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-truck-outline</v-icon>
                              Transporte
                            </td>                            <td>
                              <template v-if="movimiento.transportation === 'DRIVER'">
                              {{ movimiento.transportation }} - {{ movimiento.driver ? `${movimiento.driver.name || ''} ${movimiento.driver.lastname || ''}`.trim() : 'No especificado' }}
                              </template>
                              <template v-else-if="movimiento.transportation === 'GRUA'">
                              Grúa - {{ crane && crane.company ? crane.company : 'No especificada' }}
                              </template>
                              <template v-else>
                              {{ movimiento.transportation || 'No especificado' }}
                              </template>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-tie</v-icon>
                              Responsable de entrega
                            </td>                            <td>
                                <template v-if="movimiento.responsibleEntrega">
                                {{ movimiento.responsibleEntrega.name }} {{ movimiento.responsibleEntrega.lastname }}
                                </template>
                                <template v-else>
                                No especificado
                                </template>
                            </td>
                          </tr>
                          <tr>
                              <td class="font-weight-medium">
                                <v-icon small color="primary" class="mr-1">mdi-account-arrow-right</v-icon>
                                Responsable recepción
                              </td>
                              <td>
                                <template v-if="movimiento.status === 'completed'">
                                  <span v-if="movimiento.responsible">
                                    {{ `${movimiento.responsible.name || ''} ${movimiento.responsible.lastname || ''}`.trim() || 'No especificado' }}
                                  </span>
                                  <span v-else>No especificado</span>
                                </template>
                                <template v-else>
                                  No especificado
                                </template>
                                <!--<span v-if="movimiento.responsible?.email" class="grey--text ml-2">{{ movimiento.responsible.email }}</span>-->
                              </td>
                            </tr>
                          
                          <!-- Dealer info (solo para colaboradores) -->
                          <tr v-if="$store.getters.HAS_ROLE(['colaborator']) && movimiento.vehicle && movimiento.vehicle.dealer">
                            <td class="font-weight-medium">
                              <v-icon small :color="movimiento.vehicle.dealer.is_colaborador ? 'primary' : 'grey'" class="mr-1">
                                {{ movimiento.vehicle.dealer.is_colaborador ? 'mdi-home' : 'mdi-store' }}
                              </v-icon>
                              Dealer
                            </td>
                            <td>
                              <div class="d-flex align-center">
                                <v-avatar v-if="movimiento.vehicle.dealer.imageUrl && movimiento.vehicle.dealer.imageUrl.url" size="20" class="mr-2">
                                  <img :src="movimiento.vehicle.dealer.imageUrl.url" alt="Logo dealer">
                                </v-avatar>
                                {{ movimiento.vehicle.dealer.name }}
                                <v-chip v-if="movimiento.vehicle.dealer.is_colaborador" x-small class="ml-2" color="primary">
                                  Propio
                                </v-chip>
                              </div>
                              <small class="grey--text">{{ movimiento.vehicle.dealer.city || 'Ciudad no disponible' }}</small>
                            </td>
                          </tr>                          <tr v-if="movimiento.transportation === 'Driver'">
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-card-details</v-icon>
                              Conductor
                            </td>
                            <td>DNI: {{ movimiento.driver && movimiento.driver.document_number ? movimiento.driver.document_number : 'No especificado' }}</td>
                          </tr><tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-speedometer</v-icon>
                              Kilómetros
                            </td>
                            <td>
                              <span>Iniciales: {{ movimiento.initial_km || 'No especificado' }}</span>
                              <span v-if="movimiento.end_km" class="ml-3">
                                | Finales: {{ movimiento.end_km }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>

                    <!-- Columna derecha -->
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="40%">
                              <v-icon small :color="movimiento.vehicle_status === 'good' ? 'green' : 'red'"
                                class="mr-1">
                                {{ movimiento.vehicle_status === 'good' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                              </v-icon>
                              Estado del vehículo
                            </td>                            <td>
                              {{ movimiento.vehicle_status === 'good' ? 'Bueno' : movimiento.vehicle_status === 'damages' ? 'Dañado' : 'No especificado' }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-card-bulleted-outline</v-icon>
                              Documentación
                            </td>
                            <td>{{ movimiento.registered === 1 ? 'Matriculado' : movimiento.registered === 0 ? 'Placas Rojas' : 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-plus</v-icon>
                              Creado por
                            </td>                            <td>
                              {{ creado && creado.data ? `${creado.data.name || ''} ${creado.data.lastname || ''}`.trim() : 'No especificado' }}
                              <small class="grey--text ml-1">{{ formatDate(movimiento.created_at) }}</small>
                            </td>
                          </tr>
                          <tr v-if="movimiento.type !== 'entry'">
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-edit</v-icon>
                              Fecha de entrega
                            </td>                            
                            <td>
                              {{ movimiento.type === 'entry'
                                ? (movimiento.reception_datetime ? formatDate(movimiento.reception_datetime) : 'No especificado')
                                : (movimiento.delivery_date && (movimiento.status === 'processing' || movimiento.status === 'completed') ? formatDate(movimiento.delivery_date) : 'No especificado') }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-account-edit</v-icon>
                              Fecha de recepción
                            </td>                            
                            <td>
                              {{ movimiento.reception_datetime ? formatDate(movimiento.reception_datetime) : 'No especificado' }}
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>

                  <!-- Observaciones -->
                  <div v-if="movimiento.observations" class="mt-4">
                    <v-divider class="mb-3"></v-divider>
                    <div class="font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1">mdi-message-text</v-icon>
                      Observaciones
                    </div>
                    <div class="body-2 pa-2 grey lighten-4 rounded preserve-linebreaks">
                      {{ movimiento.observations }}
                    </div>
                  </div>

                  <!-- Información adicional -->
                  <div v-if="movimiento.additional_info || movimiento.additional_info_delivery || movimiento.additional_info_reception" class="mt-4">
                    <v-divider class="mb-3"></v-divider>
                    <div class="font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
                      Información adicional
                    </div>
                    <div class="body-2 pa-2 blue lighten-5 rounded preserve-linebreaks">
                      <div v-if="movimiento.additional_info" class="mb-2">
                        <strong>CREACIÓN PETICIÓN: </strong> {{ movimiento.additional_info }}
                      </div>
                      <div v-if="movimiento.additional_info_delivery" class="mb-2">
                        <strong>ENTREGA MOVIMIENTO: </strong> {{ movimiento.additional_info_delivery }}
                      </div>
                      <div v-if="movimiento.additional_info_reception">
                        <strong>RECEPCIÓN MOVIMIENTO: </strong> {{ movimiento.additional_info_reception }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="movimiento.equipment_status && movimiento.equipment_status.some(equipment => ((equipment.is_available === false && equipment.changed === false) || (equipment.is_available === true && equipment.changed === true)) || equipment.is_available === false)"
                    class="mt-4"
                  >
                    <v-divider class="mb-3"></v-divider>

                    <div class="font-weight-medium mb-2">
                      <v-icon small color="primary" class="mr-1">
                        mdi-information-outline
                      </v-icon>
                      Equipamiento Básico
                    </div>

                    <div class="body-2 pa-2 blue lighten-5 rounded preserve-linebreaks">
                      <div>
                        Equipamiento básico no marcado: Entrega
                        <div
                          v-for="(equipment, index) in movimiento.equipment_status"
                          :key="index"
                        >
                          <div v-if="(equipment.is_available === false && equipment.changed === false) || (equipment.is_available === true && equipment.changed === true)">
                            - {{ equipment.equipment_name }}
                          </div>
                        </div>
                        <br>
                        <div v-if="movimiento.status === 'completed'">
                          Equipamiento básico no marcado: Recepción
                          <div
                            v-for="(equipment, index) in movimiento.equipment_status"
                            :key="index"
                          >
                            <div v-if="equipment.is_available === false">
                              - {{ equipment.equipment_name }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Pestaña de información del vehículo -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="35%">
                              <v-icon small color="primary" class="mr-1">mdi-tag</v-icon>
                              Marca
                            </td>
                            <td>{{ movimiento.vehicle.brand || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-car-side</v-icon>
                              Modelo
                            </td>
                            <td>{{ movimiento.vehicle.model || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-card-bulleted</v-icon>
                              Matrícula
                            </td>
                            <td>{{ movimiento.vehicle.plate_number || 'No especificado' }}</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-medium" width="35%">
                              <v-icon small color="primary" class="mr-1">mdi-card-account-details</v-icon>
                              Número IIC
                            </td>
                            <td>{{ movimiento.vehicle.iic_number || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-identifier</v-icon>
                              Bastidor
                            </td>
                            <td>{{ movimiento.vehicle.frame_number || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-medium">
                              <v-icon small color="primary" class="mr-1">mdi-car-info</v-icon>
                              Ver ficha completa
                            </td>
                            <td>
                              <v-btn x-small outlined color="primary"
                                :to="{ name: 'VehiculoDetail', params: { id: movimiento.vehicle.id } }">
                                Ver vehículo
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Pestaña de imágenes -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <!-- Solo imágenes (filtradas por extensión) -->
                  <div v-if="imageFiles && imageFiles.length > 0">
                    <v-row>
                      <v-col v-for="(image, i) in imageFiles" :key="`image-${i}`" cols="12" sm="6" md="4" lg="3">
                        <v-card class="mx-auto image-card" outlined hover>
                          <v-img :src="image.url" height="150" cover class="cursor-pointer" @click="openImageDialog(image.url)">
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                              </v-row>
                            </template>
                            <div class="image-overlay d-flex align-center justify-center">
                              <v-icon color="white">mdi-magnify-plus</v-icon>
                            </div>
                          </v-img>
                          <!-- Chip para indicar tipo de imagen -->
                          <v-chip class="image-badge" x-small :color="image.isDamage ? 'error' : 'success'" text-color="white">
                            <v-icon left x-small>
                              {{ image.type === 'entrega' ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
                            </v-icon>
                            {{ image.isDamage ? (image.type === 'entrega' ? 'Daño Entrega' : 'Daño Recepción') : (image.type === 'entrega' ? 'Entrega' : 'Recepción') }}
                          </v-chip>
                          <!-- Botón de descarga -->
                          <v-btn 
                            class="download-button" 
                            icon 
                            x-small 
                            color="white"
                            @click.stop="downloadImage(image.url, image.isDamage ? `dano_${i + 1}` : `adicional_${i + 1}`)"
                          >
                            <v-icon small>mdi-download</v-icon>
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                  <div v-else class="text-center py-8 grey--text">
                    <v-icon size="50" color="grey lighten-2">mdi-image-off</v-icon>
                    <div class="mt-2">No hay imágenes disponibles</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Pestaña de documentos -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <!-- Solo documentos (desde additionalDocsUrls) -->
                  <div v-if="documentFiles && documentFiles.length > 0">
                    <v-list>
                      <v-list-item v-for="(doc, i) in documentFiles" :key="`doc-${i}`" class="document-item">
                        <v-list-item-avatar>
                          <v-icon :color="getFileIconColor(doc.extension || doc.url)" size="32">{{ getFileIcon(doc.extension || doc.url) }}</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">{{ doc.filename || getFileName(doc.url) }}</v-list-item-title>
                          <v-list-item-subtitle>
                            <v-chip 
                              x-small 
                              :color="doc.isDamage ? 'error' : (doc.type === 'entrega' ? 'success' : 'primary')" 
                              text-color="white" 
                              class="mr-2"
                            >
                              <v-icon left x-small>
                                {{ doc.type === 'entrega' ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
                              </v-icon>
                              {{ doc.isDamage 
                                ? (doc.type === 'entrega' ? 'Daño Entrega' : 'Daño Recepción') 
                                : (doc.type === 'entrega' ? 'Entrega' : 'Recepción') 
                              }}
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn icon color="primary" @click="downloadDocument(doc.url, doc.filename || getFileName(doc.url))">
                            <v-icon>mdi-download</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </div>
                  <div v-else class="text-center py-8 grey--text">
                    <v-icon size="50" color="grey lighten-2">mdi-file-document-off</v-icon>
                    <div class="mt-2">No hay documentos disponibles</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </template>
    </v-container>

    <!-- Componentes flotantes -->
    <!-- Formulario de edición -->
    <movimiento-form v-if="visibleMovimientoForm" v-model="editItem" @save="saveMovimiento"
      @cancel="visibleMovimientoForm = false"></movimiento-form>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Eliminar movimiento
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar este movimiento? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" @click="deleteMovimiento">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Visor de imágenes a pantalla completa -->
    <v-dialog v-model="imageViewerVisible" fullscreen>
      <v-card class="image-viewer">
        <v-btn icon class="close-button" @click="imageViewerVisible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-carousel v-if="imageFiles.length > 0" height="100vh" hide-delimiters :value="currentImageIndex"
          @input="currentImageIndex = $event">
          <v-carousel-item v-for="(image, i) in imageFiles" :key="`image-${i}`" :src="image.url"
            contain>
            <template v-slot:default>
              <div class="image-info pa-4">
                <v-chip small :color="image.isDamage ? 'error' : 'success'" dark>
                  {{ image.isDamage ? 'Daños' : 'Adicional' }}
                </v-chip>
              </div>
            </template>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MovimientoService from '@/services/MovimientoService'
import UserService from '@/services/UserService'
import moment from 'moment-timezone'
import MovimientoForm from './MovimientoForm.vue'
import GruaService from '../../services/GruaService'
import { mapGetters } from 'vuex'

export default {
  components: {
    MovimientoForm
  },

  data () {
    return {
      loading: true,
      movimiento: null,
      creado: null,
      update: null,
      crane: null,
      visibleMovimientoForm: false,
      editItem: null,
      deleteDialog: false,
      imageViewerVisible: false,
      currentImageIndex: 0,
      activeTab: 0,
      moment: moment
    }
  },

  async created () {
    await this.loadMovimientoDetails()
  },

  methods: {
    async loadMovimientoDetails () {
      try {
        this.loading = true
        const moviId = this.$route.params.movimiento_id
        const { data } = await MovimientoService.view({ id: moviId })
        this.movimiento = data.data

        // Cargar datos de usuarios
        const creadoData = await UserService.view({ id: this.movimiento.created_by })
        this.creado = creadoData.data
        if (this.movimiento.crane_id) {
          const grua = await GruaService.view({ id: this.movimiento.crane_id })
          this.crane = grua.data.data
        }
        
        const updateData = await UserService.view({ id: this.movimiento.updated_by })
        this.update = updateData.data
      } catch (error) {
        console.error('Error al cargar los detalles del movimiento:', error)
        this.$toast.error('Error al cargar los detalles del movimiento')
      } finally {
        this.loading = false
      }
    },

    showMovimientoForm (item) {
      this.editItem = item
      this.visibleMovimientoForm = true
    },

    saveMovimiento () {
      this.visibleMovimientoForm = false
      this.loadMovimientoDetails()
      this.$toast.success('Movimiento actualizado con éxito')
    },

    confirmDeleteMovimiento () {
      this.deleteDialog = true
    },

    async deleteMovimiento  () {
      try {
        this.deleteDialog = false
        const moviId = this.$route.params.movimiento_id
        await MovimientoService.delete({ id: moviId }).then((response) => {
          if (response.error) {
            this.$toast.error('Error al eliminar el movimiento')
            return
          }
          this.$toast.success('Movimiento eliminado con éxito')
          this.$router.push({ name: 'Movimiento' })
        })
      } catch (error) {
        console.error('Error al eliminar el movimiento:', error)
        this.$toast.error('Error al eliminar el movimiento')
      }
    },

    async acceptMovimiento () {
      try {
        const moviId = this.$route.params.movimiento_id

        await MovimientoService.aceptarMovimiento(moviId).then((response) => {
          if (response.error) {
            this.$toast.error('Ocurrió un error al cambiar el estado del movimiento')
            return
          }
          this.$toast.success('Estado del movimiento actualizado con éxito')
          this.loadMovimientoDetails()
        })
      } catch (error) {
        console.error('Error al cambiar el estado del movimiento:', error)
        this.$toast.error('Ocurrió un error al cambiar el estado del movimiento')
      }
    },

    getCurrentUserRoles () {
      return this.$store.getters.CURRENT_USER_ROLES || []
    },

    canChangeStatus () {
      if (!this.movimiento) return false
      if (this.movimiento.status !== 'unreported') return false
      // El colaborador no puede cambiar el estado
      if (this.getCurrentUserRoles().includes('colaborator')) return false
      return true
    },

    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },

    formatDateHours (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },

    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },

    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },

    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },

    openImageViewer (index) {
      this.currentImageIndex = index
      this.imageViewerVisible = true
    },

    openImageDialog (imageUrl) {
      // Encontrar el índice de la imagen en el array de imágenes filtrado
      const index = this.imageFiles.findIndex(img => img.url === imageUrl)
      if (index !== -1) {
        this.currentImageIndex = index
        this.imageViewerVisible = true
      }
    },

    async downloadImage (imageUrl, defaultName) {
      try {
        // Fetch la imagen como blob
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        
        // Crear URL temporal del blob
        const blobUrl = window.URL.createObjectURL(blob)
        
        // Extraer extensión de la URL o usar jpg por defecto
        const extension = imageUrl.split('.').pop().split('?')[0] || 'jpg'
        const fileName = `${defaultName}_${Date.now()}.${extension}`
        
        // Crear elemento temporal de descarga
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        
        // Limpiar
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
        
        this.$toast.success('Imagen descargada correctamente')
      } catch (error) {
        console.error('Error al descargar la imagen:', error)
        this.$toast.error('Error al descargar la imagen')
      }
    },

    async downloadDocument (docUrl, fileName) {
      try {
        // Intentar descargar con fetch primero
        try {
          const response = await fetch(docUrl, {
            mode: 'cors',
            credentials: 'include'
          })
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          
          const blob = await response.blob()
          const blobUrl = window.URL.createObjectURL(blob)
          
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
          
          this.$toast.success('Documento descargado correctamente')
        } catch (fetchError) {
          // Si fetch falla (por CORS u otro motivo), abrir en nueva pestaña
          console.warn('Fetch falló, abriendo en nueva pestaña:', fetchError)
          const link = document.createElement('a')
          link.href = docUrl
          link.target = '_blank'
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          this.$toast.success('Documento abierto en nueva pestaña')
        }
      } catch (error) {
        console.error('Error al descargar el documento:', error)
        this.$toast.error('Error al descargar el documento')
      }
    },

    isImageFile (url) {
      if (!url) return false
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const urlLower = url.toLowerCase()
      return imageExtensions.some(ext => urlLower.includes(ext))
    },

    getFileName (url) {
      if (!url) return 'documento'
      const parts = url.split('/')
      const fileName = parts[parts.length - 1]
      return fileName.split('?')[0] || 'documento'
    },

    getFileIcon (fileData) {
      // Soporta tanto extension directa como URL
      const extension = typeof fileData === 'string' 
        ? fileData.split('.').pop().toLowerCase().split('?')[0]
        : fileData
      
      const iconMap = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        txt: 'mdi-file-document-outline',
        zip: 'mdi-folder-zip',
        rar: 'mdi-folder-zip'
      }
      return iconMap[extension] || 'mdi-file-outline'
    },

    getFileIconColor (fileData) {
      // Soporta tanto extension directa como URL
      const extension = typeof fileData === 'string'
        ? fileData.split('.').pop().toLowerCase().split('?')[0]
        : fileData
      
      const colorMap = {
        pdf: 'red',
        doc: 'blue',
        docx: 'blue',
        xls: 'green',
        xlsx: 'green',
        txt: 'grey',
        zip: 'orange',
        rar: 'orange'
      }
      return colorMap[extension] || 'grey'
    }
  },

  computed: {
    allImages () {
      const images = []
      
      // Agregar imágenes de daños
      if (this.movimiento && this.movimiento.imagesUrls) {
        // Verificar si es un array o un objeto
        if (Array.isArray(this.movimiento.imagesUrls)) {
          this.movimiento.imagesUrls.forEach(img => {
            images.push({
              ...img,
              type: 'damage'
            })
          })
        } else if (typeof this.movimiento.imagesUrls === 'object') {
          // Si es un objeto, convertir a array usando Object.values()
          Object.values(this.movimiento.imagesUrls).forEach(img => {
            if (img && img.url) {
              images.push({
                ...img,
                type: 'damage'
              })
            }
          })
        }
      }
      
      // Agregar imágenes adicionales
      if (this.movimiento && this.movimiento.additionalImagesUrls) {
        // Verificar si es un array o un objeto
        if (Array.isArray(this.movimiento.additionalImagesUrls)) {
          // Si es un array, usar forEach normalmente
          this.movimiento.additionalImagesUrls.forEach(img => {
            images.push({
              ...img,
              type: 'additional'
            })
          })
        } else if (typeof this.movimiento.additionalImagesUrls === 'object') {
          // Si es un objeto, convertir a array usando Object.values()
          Object.values(this.movimiento.additionalImagesUrls).forEach(img => {
            if (img && img.url) {
              images.push({
                ...img,
                type: 'additional'
              })
            }
          })
        }
      }
      
      return images
    },

    // Filtrar solo archivos de imagen
    imageFiles () {
      const images = []
      
      // Procesar imágenes de daños
      if (this.movimiento && this.movimiento.imagesUrls) {
        const imagesArray = Array.isArray(this.movimiento.imagesUrls) 
          ? this.movimiento.imagesUrls 
          : Object.values(this.movimiento.imagesUrls || {})
        
        imagesArray.forEach(img => {
          if (img && img.url && this.isImageFile(img.url)) {
            images.push({
              ...img,
              isDamage: true
            })
          }
        })
      }
      
      // Procesar imágenes adicionales
      if (this.movimiento && this.movimiento.additionalImagesUrls) {
        const additionalArray = Array.isArray(this.movimiento.additionalImagesUrls)
          ? this.movimiento.additionalImagesUrls
          : Object.values(this.movimiento.additionalImagesUrls || {})
        
        additionalArray.forEach(img => {
          if (img && img.url && this.isImageFile(img.url)) {
            images.push({
              ...img,
              isDamage: false
            })
          }
        })
      }
      
      return images
    },

    // Obtener documentos adicionales y de daños
    documentFiles () {
      const documents = []
      
      // Agregar documentos adicionales
      if (this.movimiento && this.movimiento.additionalDocsUrls) {
        const additionalDocsArray = Array.isArray(this.movimiento.additionalDocsUrls)
          ? this.movimiento.additionalDocsUrls
          : Object.values(this.movimiento.additionalDocsUrls || {})
        
        additionalDocsArray.forEach(doc => {
          if (doc && doc.url) {
            documents.push({
              ...doc,
              isDamage: false
            })
          }
        })
      }
      
      // Agregar documentos de daños
      if (this.movimiento && this.movimiento.damageDocsUrls) {
        const damageDocsArray = Array.isArray(this.movimiento.damageDocsUrls)
          ? this.movimiento.damageDocsUrls
          : Object.values(this.movimiento.damageDocsUrls || {})
        
        damageDocsArray.forEach(doc => {
          if (doc && doc.url) {
            documents.push({
              ...doc,
              isDamage: true
            })
          }
        })
      }
      
      return documents
    },

    additionalImagesArray () {
      if (!this.movimiento || !this.movimiento.additionalImagesUrls) {
        return []
      }
      
      // Verificar si es un array o un objeto
      if (Array.isArray(this.movimiento.additionalImagesUrls)) {
        return this.movimiento.additionalImagesUrls
      } else {
        // Si es un objeto, convertir a array usando Object.values()
        return Object.values(this.movimiento.additionalImagesUrls)
      }
    },

    ...mapGetters(['HAS_PERMISSION']),

    canEditMovimiento() {
      return this.HAS_PERMISSION('editar_movimiento')
    },

    canDeleteMovimiento() {
      return this.HAS_PERMISSION('eliminar_movimiento')
    }
  }
}
</script>

<style scoped>
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-badge {
  position: absolute !important;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.download-button {
  position: absolute !important;
  bottom: 8px;
  right: 8px;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.6) !important;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-card:hover .download-button {
  opacity: 1;
}

.v-img:hover .image-overlay {
  opacity: 1;
}

.cursor-pointer {
  cursor: pointer;
}

.image-viewer {
  background-color: rgba(0, 0, 0, 0.95);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
}

.v-data-table td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.preserve-linebreaks {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.document-item {
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.3s ease;
}

.document-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.document-item:last-child {
  border-bottom: none;
}
</style>
